import { useEffect, useState } from 'react';
import {
  BODY_TYPE_LABEL,
  INGEST_STATUS_LABEL,
  INGEST_STATUSES,
  SOURCE_VIA_LABEL,
  USERBOT_STATUS_LABEL,
  type IngestedPostRow,
  type IngestStatus,
  type SourceChatRow,
  type SourceVia,
  type UserbotView,
} from 'shared';
import { api } from '../api';
import { alert, confirm, haptic } from '../telegram';

/**
 * Импорт из чатов логистов.
 *
 * Сверху — какие чаты читаем (бот должен быть в чате). Ниже — что из них
 * вычитано и разобрано: каждый пост можно открыть в форме публикации,
 * пропустить или переразобрать текущим парсером.
 */
export default function AdminIngest({
  onOpenDraft,
}: {
  onOpenDraft?: (draftId: string) => void;
}) {
  const [chats, setChats] = useState<SourceChatRow[]>([]);
  const [posts, setPosts] = useState<IngestedPostRow[]>([]);
  const [status, setStatus] = useState<IngestStatus | ''>('PARSED');
  const [newChat, setNewChat] = useState('');
  const [newVia, setNewVia] = useState<SourceVia>('BOT');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  /* userbot-панель */
  const [ub, setUb] = useState<UserbotView | null>(null);
  const [apiId, setApiId] = useState('');
  const [apiHash, setApiHash] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');

  async function loadUb() {
    try {
      setUb(await api.admin.userbot());
    } catch {
      setUb(null);
    }
  }
  useEffect(() => {
    void loadUb();
    const t = setInterval(() => void loadUb(), 4000);
    return () => clearInterval(t);
  }, []);

  async function loadChats() {
    try {
      setChats((await api.admin.sourceChats()).items);
    } catch {
      /* тихо */
    }
  }
  async function loadPosts() {
    try {
      setPosts((await api.admin.ingestFeed(status || undefined)).items);
    } catch {
      setPosts([]);
    }
  }

  useEffect(() => {
    void loadChats();
  }, []);
  useEffect(() => {
    void loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function addChat() {
    const v = newChat.trim();
    if (!v) return;
    setBusy(true);
    try {
      await api.admin.addSourceChat(v, newVia);
      setNewChat('');
      await loadChats();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function ubAction(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      await loadUb();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function patchChat(c: SourceChatRow, data: { enabled?: boolean; autoImport?: boolean }) {
    setChats((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...data } : x)));
    try {
      await api.admin.patchSourceChat(c.id, data);
    } catch (e) {
      await alert((e as Error).message);
      void loadChats();
    }
  }

  async function removeChat(c: SourceChatRow) {
    if (!(await confirm(`Убрать «${c.title ?? c.chatId}» из мониторинга? Разобранные посты удалятся.`))) {
      return;
    }
    try {
      await api.admin.removeSourceChat(c.id);
      await loadChats();
      void loadPosts();
    } catch (e) {
      await alert((e as Error).message);
    }
  }

  async function skip(p: IngestedPostRow) {
    try {
      await api.admin.skipIngest(p.id);
      setPosts((prev) => prev.filter((x) => x.id !== p.id));
    } catch (e) {
      await alert((e as Error).message);
    }
  }

  async function reparse(p: IngestedPostRow) {
    try {
      await api.admin.reparseIngest(p.id);
      await loadPosts();
      haptic();
    } catch (e) {
      await alert((e as Error).message);
    }
  }

  return (
    <>
      <h3>Аккаунт-читатель</h3>
      <p className="meta">
        В чаты конкурентов бота не добавить — их читает отдельный аккаунт
        Telegram (заведите пустой номер). Автоматизация юзер-аккаунта против
        правил Telegram: используйте не личный номер.
      </p>

      {ub && (
        <div className="card" style={{ marginTop: 'var(--s2)' }}>
          <div className="row">
            <strong>{USERBOT_STATUS_LABEL[ub.status]}</strong>
            {ub.phone && <span className="meta">{ub.phone}</span>}
            {ub.status === 'ONLINE' && (
              <span className="badge badge--verified">на связи</span>
            )}
          </div>
          {ub.lastError && <div className="error">{ub.lastError}</div>}

          {!ub.hasCreds && (
            <>
              <div className="meta" style={{ marginTop: 'var(--s2)' }}>
                Ключи с my.telegram.org → «API development tools».
              </div>
              <div className="field-row">
                <div className="field">
                  <label>API ID</label>
                  <input value={apiId} onChange={(e) => setApiId(e.target.value)} />
                </div>
                <div className="field">
                  <label>API Hash</label>
                  <input value={apiHash} onChange={(e) => setApiHash(e.target.value)} />
                </div>
              </div>
              <button
                type="button"
                className="secondary"
                disabled={busy || !apiId.trim() || !apiHash.trim()}
                onClick={() =>
                  void ubAction(() => api.admin.userbotCreds(apiId.trim(), apiHash.trim()))
                }
              >
                Сохранить ключи
              </button>
            </>
          )}

          {ub.hasCreds && ub.status !== 'ONLINE' && ub.status !== 'NEED_CODE' && ub.status !== 'NEED_PASSWORD' && (
            <div className="offer__row" style={{ marginTop: 'var(--s2)' }}>
              <input
                placeholder="+79991234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <button
                type="button"
                className="secondary"
                disabled={busy || !phone.trim()}
                onClick={() => void ubAction(() => api.admin.userbotLogin(phone.trim()))}
              >
                Выслать код
              </button>
            </div>
          )}

          {ub.status === 'NEED_CODE' && (
            <div className="offer__row" style={{ marginTop: 'var(--s2)' }}>
              <input
                placeholder="код из Telegram"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <button
                type="button"
                className="secondary"
                disabled={busy || !code.trim()}
                onClick={() =>
                  void ubAction(async () => {
                    await api.admin.userbotCode(code.trim());
                    setCode('');
                  })
                }
              >
                Войти
              </button>
            </div>
          )}

          {ub.status === 'NEED_PASSWORD' && (
            <div className="offer__row" style={{ marginTop: 'var(--s2)' }}>
              <input
                type="password"
                placeholder="пароль 2FA"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
              />
              <button
                type="button"
                className="secondary"
                disabled={busy || !pw}
                onClick={() =>
                  void ubAction(async () => {
                    await api.admin.userbotPassword(pw);
                    setPw('');
                  })
                }
              >
                Продолжить
              </button>
            </div>
          )}

          {(ub.status === 'ONLINE' || ub.status === 'ERROR') && (
            <button
              type="button"
              className="danger btn--compact"
              style={{ marginTop: 'var(--s2)' }}
              onClick={() => void ubAction(() => api.admin.userbotLogout())}
            >
              Отключить аккаунт
            </button>
          )}
        </div>
      )}

      <h3 className="sect">Каналы</h3>
      <p className="meta">
        Через бота — он должен быть в чате (в канале администратором, в
        супергруппе с выключенным privacy mode). Через аккаунт — читает
        аккаунт-читатель, он должен сам состоять в канале.
      </p>

      <div className="chips" style={{ marginTop: 'var(--s2)' }}>
        {(['BOT', 'USERBOT'] as SourceVia[]).map((v) => (
          <button
            key={v}
            type="button"
            className={newVia === v ? 'chip chip--on' : 'chip'}
            onClick={() => setNewVia(v)}
          >
            {SOURCE_VIA_LABEL[v]}
          </button>
        ))}
      </div>

      <div className="offer__row" style={{ marginTop: 'var(--s2)' }}>
        <input
          placeholder="-1001856704539 или @username"
          value={newChat}
          onChange={(e) => setNewChat(e.target.value)}
        />
        <button
          type="button"
          className="secondary"
          disabled={busy || !newChat.trim()}
          onClick={() => void addChat()}
        >
          Добавить
        </button>
      </div>

      {chats.length === 0 && <div className="meta">Чатов пока нет.</div>}

      {chats.map((c) => (
        <div key={c.id} className="card" style={{ marginTop: 'var(--s2)' }}>
          <div className="row">
            <strong>{c.title ?? c.ref ?? c.chatId}</strong>
            <span className="badge">{SOURCE_VIA_LABEL[c.via]}</span>
            {c.botInChat ? (
              <span className="badge badge--verified">доступен</span>
            ) : (
              <span className="badge badge--warn">
                {c.via === 'BOT' ? 'бота нет в чате' : 'не опознан / нет доступа'}
              </span>
            )}
          </div>
          <div className="meta">
            {c.ref ?? c.chatId} · вычитано {c.postsTotal}, стало заявками {c.postsImported}
            {c.addedByName ? ` · ${c.addedByName}` : ''}
          </div>
          <div className="chips" style={{ marginTop: 'var(--s2)' }}>
            <button
              type="button"
              className={c.enabled ? 'chip chip--on' : 'chip'}
              onClick={() => void patchChat(c, { enabled: !c.enabled })}
            >
              {c.enabled ? 'Читаем' : 'Выключен'}
            </button>
            <button
              type="button"
              className={c.autoImport ? 'chip chip--on' : 'chip'}
              onClick={() => void patchChat(c, { autoImport: !c.autoImport })}
              data-tip="Публиковать распознанное сразу через модерацию"
            >
              Автопубликация
            </button>
            <button type="button" className="chip" onClick={() => void removeChat(c)}>
              Убрать
            </button>
          </div>
        </div>
      ))}

      <h3 className="sect">Посты</h3>

      <div className="chips">
        {(['PARSED', ...INGEST_STATUSES.filter((s) => s !== 'PARSED')] as IngestStatus[]).map(
          (s) => (
            <button
              key={s}
              type="button"
              className={status === s ? 'chip chip--on' : 'chip'}
              onClick={() => setStatus(status === s ? '' : s)}
            >
              {INGEST_STATUS_LABEL[s]}
            </button>
          ),
        )}
      </div>

      {posts.length === 0 && (
        <div className="empty" style={{ marginTop: 'var(--s3)' }}>
          Постов нет.
        </div>
      )}

      {posts.map((p) => (
        <div key={p.id} className="card" style={{ marginTop: 'var(--s2)' }}>
          <div className="meta">
            {p.chatTitle ?? p.chatId} ·{' '}
            {new Date(p.createdAt).toLocaleString('ru-RU', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })}{' '}
            · <span className="badge">{INGEST_STATUS_LABEL[p.status]}</span>
          </div>

          {p.parsings.map((pp, i) => (
            <div key={i} className="row" style={{ marginTop: 'var(--s2)', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div>
                  <strong>
                    {(pp.parsed.from?.address ?? pp.parsed.fromText ?? '—')} →{' '}
                    {(pp.parsed.to?.address ?? pp.parsed.toText ?? '—')}
                  </strong>
                </div>
                <div className="meta">
                  {[
                    pp.parsed.title,
                    pp.parsed.weightKg ? `${(pp.parsed.weightKg / 1000).toFixed(1)} т` : null,
                    pp.parsed.bodyTypes.map((b) => BODY_TYPE_LABEL[b]).join('/') || null,
                    pp.parsed.price ? `${pp.parsed.price} ${pp.parsed.currency ?? ''}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'мало полей'}
                </div>
              </div>
              {pp.draftId && onOpenDraft && (
                <button
                  type="button"
                  className="secondary btn--compact"
                  onClick={() => onOpenDraft(pp.draftId!)}
                >
                  В форму
                </button>
              )}
            </div>
          ))}

          <div className="action-row" style={{ marginTop: 'var(--s2)' }}>
            <button
              type="button"
              className="secondary btn--compact"
              onClick={() => setExpanded(expanded === p.id ? null : p.id)}
            >
              {expanded === p.id ? 'Скрыть текст' : 'Исходный текст'}
            </button>
            <button
              type="button"
              className="secondary btn--compact"
              onClick={() => void reparse(p)}
            >
              Переразобрать
            </button>
            <button
              type="button"
              className="danger btn--compact"
              onClick={() => void skip(p)}
            >
              Пропустить
            </button>
          </div>

          {expanded === p.id && (
            <div
              style={{ whiteSpace: 'pre-wrap', marginTop: 'var(--s2)' }}
              className="meta"
            >
              {p.rawText}
            </div>
          )}
        </div>
      ))}
    </>
  );
}
